import React, { useEffect, useRef } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Checkbox from '@amzn/awsui-components-console/checkbox';
import Form from '@amzn/awsui-components-console/form';
import FormField from '@amzn/awsui-components-console/form-field';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';

import Dialog from './dialog';

export default function FeedbackDialog({ 
  onDismiss, 
  onSubmit, 
  userMessage, 
  aiResponse, 
  sessionId,
  isPositive = false
}: { 
  onDismiss: () => void; 
  onSubmit: (data: any) => void;
  userMessage: string;
  aiResponse: string;
  sessionId: string | null;
  isPositive?: boolean;
}) {
  const [feedbackOptions, setFeedbackOptions] = React.useState({
    harmful: false,
    incomplete: false,
    inaccurate: false,
    other: false,
  });
  const [feedbackText, setFeedbackText] = React.useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Store the element that had focus before dialog opened
    triggerRef.current = document.activeElement as HTMLElement;

    // Focus the dialog container when it opens
    dialogRef.current?.focus();

    // Cleanup: Return focus to the trigger element when dialog closes
    return () => {
      triggerRef.current?.focus();
    };
  }, [dialogRef]);

  const selectOption = (option: string, checked: boolean) =>
    setFeedbackOptions({ ...feedbackOptions, [option]: checked });

  const isSubmittable = isPositive || Object.values(feedbackOptions).some(Boolean) || feedbackText.length > 0;

  const submitFeedback = () => {
    if (!isSubmittable) {
      return;
    }

    const feedbackData = {
      session_id: sessionId,
      prompt: userMessage,
      response: aiResponse,
      is_helpful: isPositive,
      notes: feedbackText || null,
      not_helpful_reason: isPositive ? null : Object.keys(feedbackOptions).find(key => feedbackOptions[key as keyof typeof feedbackOptions]) || null,
    };

    onSubmit(feedbackData);
  };

  return (
    <Dialog
      ariaLabel="Feedback dialog"
      ref={dialogRef}
      onDismiss={onDismiss}
      footer={
        <div style={{ display: 'flex', flexDirection: 'row-reverse', gap: '4px' }}>
          <Button
            data-testid="feedback-submit-button"
            onClick={submitFeedback}
            ariaLabel="Submit form"
            disabled={!isSubmittable}
          >
            Submit
          </Button>

          <Button variant="link" onClick={onDismiss} ariaLabel="Close dialog" data-testid="feedback-close-button">
            Close
          </Button>
        </div>
      }
    >
      <Form
        header={
          <Box variant="h4">
            {isPositive ? 'Additional feedback' : 'Tell us more'} - <i>optional</i>
          </Box>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          {!isPositive && (
            <FormField label="What did you dislike about the response?">
              <SpaceBetween size="l" direction="horizontal">
                <Checkbox
                  data-testid="feedback-checkbox-harmful"
                  checked={feedbackOptions.harmful}
                  onChange={({ detail }) => selectOption('harmful', detail.checked)}
                >
                  Harmful
                </Checkbox>
                <Checkbox
                  checked={feedbackOptions.incomplete}
                  onChange={({ detail }) => selectOption('incomplete', detail.checked)}
                >
                  Incomplete
                </Checkbox>
                <Checkbox
                  checked={feedbackOptions.inaccurate}
                  onChange={({ detail }) => selectOption('inaccurate', detail.checked)}
                >
                  Inaccurate
                </Checkbox>
                <Checkbox
                  checked={feedbackOptions.other}
                  onChange={({ detail }) => selectOption('other', detail.checked)}
                >
                  Other
                </Checkbox>
              </SpaceBetween>
            </FormField>
          )}

          <FormField label={isPositive ? "What did you find helpful about this response?" : "Additional notes"} stretch={true}>
            <Textarea
              rows={3}
              onChange={({ detail }) => setFeedbackText(detail.value)}
              value={feedbackText}
              placeholder={isPositive ? 'Tell us what worked well...' : 'Additional feedback'}
            />
          </FormField>
        </SpaceBetween>
      </Form>
    </Dialog>
  );
}